import React, { useState } from "react";
import { auth } from "./firebase";
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, createUserWithEmailAndPassword, signOut } from "firebase/auth";
import { Button, TextField, Box, Typography, CircularProgress, Tabs, Tab, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useTranslation } from 'react-i18next';
import { config } from './config/env';

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [error, setError] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState(""); // Email für die Erfolgsmeldung
  const [resendingEmail, setResendingEmail] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState("");

  const getAuthErrorMessage = (
    error: any,
    options?: { isRegistering?: boolean; isGoogle?: boolean }
  ) => {
    const code = typeof error?.code === 'string' ? error.code : '';

    const codeToKey: Record<string, string> = {
      'auth/invalid-email': 'login.error.invalid.email',
      'auth/user-not-found': 'login.error.login.credentials',
      'auth/wrong-password': 'login.error.login.credentials',
      'auth/invalid-credential': 'login.error.login.credentials',
      'auth/email-already-in-use': 'login.error.register.email.in.use',
      'auth/weak-password': 'login.error.register.weak.password',
      'auth/too-many-requests': 'login.error.too.many.requests',
      'auth/network-request-failed': 'login.error.network',
      'auth/popup-closed-by-user': 'login.error.google.popup.closed',
      'auth/popup-blocked': 'login.error.google.popup.blocked',
      'auth/cancelled-popup-request': 'login.error.google.popup.cancelled',
      'auth/unauthorized-domain': 'login.error.google.unauthorized.domain',
      'auth/invalid-continue-uri': 'login.error.google.invalid.continue.uri',
      'auth/operation-not-allowed': 'login.error.operation.not.allowed',
    };

    const resolvedKey = codeToKey[code];
    if (resolvedKey) {
      return t(resolvedKey);
    }

    if (options?.isGoogle) return t('login.error.google');
    if (options?.isRegistering) return t('login.error.registration');
    return t('login.error.login');
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailLoading(true);
    setError("");
    
    // E-Mail validieren und trimmen
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError(t('login.email.required') || 'E-Mail-Adresse ist erforderlich');
      setEmailLoading(false);
      return;
    }
    
    // E-Mail-Format validieren
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError(t('login.email.invalid') || 'Ungültige E-Mail-Adresse');
      setEmailLoading(false);
      return;
    }
    
    try {
      if (isRegistering) {
        // Registrierung - Passwort-Bestätigung prüfen
        if (pw !== pwConfirm) {
          setError(t('login.password.mismatch'));
          setEmailLoading(false);
          return;
        }
        if (pw.length < 6) {
          setError(t('login.password.too.short'));
          setEmailLoading(false);
          return;
        }
        
        // Account erstellen
        await createUserWithEmailAndPassword(auth, trimmedEmail, pw);
        console.log('✅ User registration successful:', trimmedEmail);
        
        // Setze Sprache für Email-Verifizierung basierend auf Browsersprache
        const browserLanguage = navigator.language.toLowerCase();
        let emailLanguage = 'de'; // Default: Deutsch
        
        if (browserLanguage.startsWith('en')) {
          emailLanguage = 'en';
        } else if (browserLanguage.startsWith('fr')) {
          emailLanguage = 'fr';
        } else if (browserLanguage.startsWith('it')) {
          emailLanguage = 'it';
        }
        
        auth.languageCode = emailLanguage;
        console.log('🌐 Email-Sprache gesetzt auf:', emailLanguage);
        
        // Email-Verifizierung über Backend senden (SMTP)
        try {
          const verificationResponse = await fetch(`${config.backend.baseUrl}/api/auth/send-email-verification`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              email: trimmedEmail,
              language: emailLanguage
            })
          });
          
          if (verificationResponse.ok) {
            const verificationData = await verificationResponse.json();
            console.log('📧 Verifizierungs-Email gesendet an:', trimmedEmail, 'in Sprache:', emailLanguage, 'Status:', verificationData.emailSent ? 'gesendet' : 'SMTP nicht konfiguriert');
          } else {
            console.warn('⚠️ Fehler beim Senden der Verifizierungs-E-Mail');
          }
        } catch (verificationError) {
          console.error('❌ Fehler beim Senden der Verifizierungs-E-Mail:', verificationError);
        }
        
        // Benutzer ausloggen, bis Email verifiziert ist
        await signOut(auth);
        
        // Zeige Erfolgsmeldung
        setVerifiedEmail(trimmedEmail); // Email für Anzeige speichern
        setVerificationSent(true);
        setEmail('');
        setPw('');
        setPwConfirm('');
        
        // Wechsel zurück zum Login-Tab
        setIsRegistering(false);
        
      } else {
        // Anmeldung - prüfe ob Email verifiziert ist
        const userCredential = await signInWithEmailAndPassword(auth, trimmedEmail, pw);
        
        // Prüfe Email-Verifizierung
        if (!userCredential.user.emailVerified) {
          // Benutzer ist nicht verifiziert - ausloggen
          await signOut(auth);
          setError(t('login.verification.required'));
          setEmailLoading(false);
          return;
        }
        
        console.log('✅ Email login successful:', trimmedEmail);
      }
    } catch (error: any) {
      console.error('❌ Auth error:', error);
      setError(getAuthErrorMessage(error, { isRegistering }));
    } finally {
      setEmailLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setError("");
    
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      console.log('✅ Google login successful');
    } catch (error: any) {
      console.error('❌ Google login error:', error);
      setError(getAuthErrorMessage(error, { isGoogle: true }));
    } finally {
      console.log('🔄 Setting googleLoading to false');
      setGoogleLoading(false);
    }
  };

  // Email erneut senden
  const handleResendVerificationEmail = async () => {
    setResendingEmail(true);
    setError("");
    
    try {
      // Versuche temporär anzumelden um Email zu senden
      const tempPw = prompt(t('login.verification.password.prompt'));
      if (!tempPw) {
        setResendingEmail(false);
        return;
      }
      
      await signInWithEmailAndPassword(auth, verifiedEmail, tempPw);
      
      // Setze Sprache für Email-Verifizierung basierend auf Browsersprache
      const browserLanguage = navigator.language.toLowerCase();
      let emailLanguage = 'de'; // Default: Deutsch
      
      if (browserLanguage.startsWith('en')) {
        emailLanguage = 'en';
      } else if (browserLanguage.startsWith('fr')) {
        emailLanguage = 'fr';
      } else if (browserLanguage.startsWith('it')) {
        emailLanguage = 'it';
      }
      
      auth.languageCode = emailLanguage;
      
      // Email-Verifizierung über Backend senden (SMTP)
      try {
        const verificationResponse = await fetch(`${config.backend.baseUrl}/api/auth/send-email-verification`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: verifiedEmail,
            language: emailLanguage
          })
        });
        
        if (verificationResponse.ok) {
          const verificationData = await verificationResponse.json();
          console.log('📧 Verifizierungs-Email erneut gesendet an:', verifiedEmail, 'Status:', verificationData.emailSent ? 'gesendet' : 'SMTP nicht konfiguriert');
        }
      } catch (verificationError) {
        console.error('❌ Fehler beim Senden der Verifizierungs-E-Mail:', verificationError);
      }
      
      await signOut(auth);
      
      setError('✅ ' + t('login.verification.resend.success'));
      console.log('📧 Verifizierungs-Email erneut gesendet an:', verifiedEmail, 'in Sprache:', emailLanguage);
    } catch (error: any) {
      console.error('❌ Fehler beim erneuten Senden:', error);
      setError(t('login.verification.resend.error'));
    } finally {
      setResendingEmail(false);
    }
  };

  // Passwort-Felder zurücksetzen beim Tab-Wechsel
  const handleTabChange = (e: React.SyntheticEvent, newValue: number) => {
    setIsRegistering(newValue === 1);
    setPw("");
    setPwConfirm("");
    setError("");
    setVerificationSent(false);
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center',
      px: { xs: 2, sm: 3 },
      pt: { xs: 2, sm: 4 },
      pb: 4,
    }}>
      <Box sx={{ 
        maxWidth: 400, 
        width: '100%',
      }}>
      <Tabs value={isRegistering ? 1 : 0} onChange={handleTabChange} centered sx={{ mb: 3 }}>
        <Tab label={t('login.tab.login')} />
        <Tab label={t('login.tab.register')} />
      </Tabs>

      {/* Verifizierungs-Erfolgsmeldung */}
      {verificationSent && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            ✅ {t('login.verification.sent.title')}
          </Typography>
          <Typography variant="body2">
            {t('login.verification.sent.message', { email: verifiedEmail })}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontSize: '0.875rem', fontWeight: 600, color: '#000000' }}>
            ⚠️ {t('login.verification.spam.warning')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontSize: '0.875rem', color: 'text.secondary' }}>
            {t('login.verification.after.message')}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={handleResendVerificationEmail}
            disabled={resendingEmail}
            sx={{ mt: 2 }}
            startIcon={resendingEmail ? <CircularProgress size={16} /> : null}
          >
            {resendingEmail ? t('login.verification.resending') : t('login.verification.resend.button')}
          </Button>
        </Alert>
      )}

      <form onSubmit={handleEmailLogin}>
        <TextField 
          label={t('login.email')} 
          value={email} 
          onChange={e => {
            setEmail(e.target.value);
            setVerificationSent(false); // Zurücksetzen wenn Benutzer neue Email eingibt
          }} 
          fullWidth 
          sx={{ mb: 2 }}
          disabled={emailLoading || googleLoading}
        />
        <TextField 
          label={t('login.password')} 
          type="password" 
          value={pw} 
          onChange={e => setPw(e.target.value)} 
          fullWidth 
          sx={{ mb: 2 }}
          disabled={emailLoading || googleLoading}
          helperText={isRegistering ? t('login.password.requirements') : ''}
          error={isRegistering && pw !== "" && pw.length < 6}
        />
        {!isRegistering && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button size="small" variant="text" onClick={() => { setShowReset(true); setResetEmail(email); setResetMessage(""); }}>
              {t('login.forgot.password') || 'Passwort vergessen?'}
            </Button>
          </Box>
        )}
        {isRegistering && (
          <>
            <TextField 
              label={t('login.password.confirm')} 
              type="password" 
              value={pwConfirm} 
              onChange={e => setPwConfirm(e.target.value)} 
              fullWidth 
              sx={{ mb: 2 }}
              disabled={emailLoading || googleLoading}
              error={pwConfirm !== "" && pw !== pwConfirm}
              helperText={pwConfirm !== "" && pw !== pwConfirm ? t('login.password.mismatch') : ""}
            />
            
            {/* Passwort-Stärke-Indikator */}
            {pw && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  {t('login.password.strength')}:
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                  <Box sx={{ 
                    flex: 1, 
                    height: 4, 
                    bgcolor: pw.length >= 6 ? '#4caf50' : '#e0e0e0',
                    borderRadius: 1,
                    transition: 'all 0.3s ease'
                  }} />
                  <Box sx={{ 
                    flex: 1, 
                    height: 4, 
                    bgcolor: pw.length >= 8 ? '#4caf50' : '#e0e0e0',
                    borderRadius: 1,
                    transition: 'all 0.3s ease'
                  }} />
                  <Box sx={{ 
                    flex: 1, 
                    height: 4, 
                    bgcolor: pw.length >= 10 && /[A-Z]/.test(pw) && /[0-9]/.test(pw) ? '#4caf50' : '#e0e0e0',
                    borderRadius: 1,
                    transition: 'all 0.3s ease'
                  }} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                  <Typography variant="caption" sx={{ 
                    color: pw.length >= 6 ? '#4caf50' : '#9e9e9e',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    {pw.length >= 6 ? '✓' : '○'} {t('login.password.min.length')}
                  </Typography>
                  <Typography variant="caption" sx={{ 
                    color: pw.length >= 8 ? '#4caf50' : '#9e9e9e',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    {pw.length >= 8 ? '✓' : '○'} {t('login.password.recommended')}
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        )}
        <Button 
          type="submit" 
          variant="outlined" 
          fullWidth 
          disabled={emailLoading || googleLoading || (isRegistering && (pw !== pwConfirm || pw.length < 6))}
          startIcon={emailLoading ? <CircularProgress size={20} /> : null}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white !important',
            borderColor: 'rgba(255,255,255,0.3)',
            fontWeight: 600,
            fontSize: '1rem',
            textTransform: 'none',
            padding: '12px 24px',
            borderRadius: 3,
            transition: 'all 0.3s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              borderColor: 'rgba(255,255,255,0.5)',
              color: 'white !important',
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            },
            '&:disabled': {
              opacity: 0.7,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white !important',
              transform: 'none',
            },
          }}
        >
          {emailLoading ? (isRegistering ? t('login.registering') : t('login.logging.in')) : (isRegistering ? t('login.register.button') : t('login.submit'))}
        </Button>
      </form>
      
      {!isRegistering && (
        <Button 
          onClick={handleGoogleLogin} 
          variant="outlined" 
          fullWidth 
          sx={{ 
            mt: 2,
            px: 3,
            py: 1.5,
            borderRadius: 3,
            borderColor: '#667eea',
            color: '#757575',
            fontWeight: 600,
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: '#764ba2',
              backgroundColor: '#f5f5f5',
              transform: 'translateY(-1px)',
            },
          }}
          disabled={emailLoading || googleLoading}
          startIcon={googleLoading ? <CircularProgress size={20} /> : null}
        >
          {googleLoading ? t('login.google.logging.in') : t('login.google')}
        </Button>
      )}
      
      {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}

      {/* Passwort-Reset Dialog */}
      <Dialog open={showReset} onClose={() => setShowReset(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('login.reset.title') || 'Passwort zurücksetzen'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            {t('login.reset.info') || 'Gib deine E‑Mail ein. Falls ein Konto existiert, senden wir dir Anweisungen zu.'}
          </Typography>
          <TextField
            label={t('login.email')}
            key={`reset-email-${resetEmail || ''}`}
            defaultValue={resetEmail}
            onBlur={(e) => {
              const nextValue = e.target.value;
              if (nextValue !== resetEmail) setResetEmail(nextValue);
            }}
            fullWidth
            autoFocus
            disabled={resetLoading}
          />
          {resetMessage && (
            <Alert severity="success" sx={{ mt: 2 }}>{resetMessage}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowReset(false)}
            sx={{
              color: '#757575',
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            {t('common.cancel') || 'Abbrechen'}
          </Button>
          <Button
            onClick={async () => {
              setResetLoading(true);
              setResetMessage("");
              try {
                const response = await fetch(`${config.backend.baseUrl}/api/auth/request-password-reset`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Accept-Language': navigator.language
                  },
                  body: JSON.stringify({ email: resetEmail.trim() })
                });
                if (response.ok) {
                  setResetMessage(t('login.reset.sent') || 'Wenn ein Konto existiert, haben wir dir eine E‑Mail gesendet.');
                } else {
                  setResetMessage(t('login.reset.sent') || 'Wenn ein Konto existiert, haben wir dir eine E‑Mail gesendet.');
                }
              } catch (e) {
                setResetMessage(t('login.reset.sent') || 'Wenn ein Konto existiert, haben wir dir eine E‑Mail gesendet.');
              } finally {
                setResetLoading(false);
              }
            }}
            variant="contained"
            disabled={resetLoading || !resetEmail.includes('@')}
            startIcon={resetLoading ? <CircularProgress size={16} /> : null}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white !important',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
              '&:disabled': {
                opacity: 0.7,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white !important',
              },
            }}
          >
            {t('login.reset.submit') || 'E-Mail senden'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
} 